import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lightbulb, Inbox } from 'lucide-react';
import { useRecommendations } from '../../hooks/useRecommendations';
import type { RecommendationLevel } from '../../api/recommendations';
import { ChartCard } from '../ui/ChartCard';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';

const PRIORITY_ORDER: Record<string, number> = { high: 0, medium: 1, low: 2 };

function levelTone(level: RecommendationLevel): 'danger' | 'warning' | 'success' {
  if (level === 'high') return 'danger';
  if (level === 'medium') return 'warning';
  return 'success';
}

export function TopRecommendations() {
  const navigate = useNavigate();
  const { data, isLoading } = useRecommendations({ status: 'generated', per_page: 20 });

  const top = useMemo(() => {
    const items = data?.data ?? [];
    return [...items]
      .sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9))
      .slice(0, 4);
  }, [data]);

  return (
    <ChartCard
      title="Top Recommendations"
      actions={<Button variant="ghost" size="sm" onClick={() => navigate('/recommendations')}>View all</Button>}
    >
      {isLoading ? (
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-14 animate-pulse rounded-lg bg-bg" />
          ))}
        </div>
      ) : top.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-8 text-center">
          <Inbox size={28} className="text-text-muted" />
          <p className="text-[16px] font-medium text-text-primary">No pending recommendations</p>
          <p className="text-[15px] text-text-secondary">Run a store audit to generate fresh recommendations.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-1">
          {top.map((rec) => (
            <div key={rec.id} className="flex items-center justify-between gap-3 rounded-lg py-2.5">
              <div className="flex min-w-0 items-center gap-3">
                <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${rec.priority === 'high' ? 'bg-warning-subtle text-warning' : 'bg-primary-subtle text-primary'}`}>
                  <Lightbulb size={18} />
                </span>
                <div className="flex min-w-0 flex-col gap-0.5">
                  <span className="truncate text-[16px] font-semibold text-text-primary">{rec.title}</span>
                  <div className="flex items-center gap-2">
                    <Badge tone={levelTone(rec.impact)}>{rec.impact_label} impact</Badge>
                    <span className="text-[15px] text-text-secondary">Effort: {rec.effort_label}</span>
                  </div>
                </div>
              </div>
              <Button size="sm" onClick={() => navigate('/recommendations')}>Take Action</Button>
            </div>
          ))}
        </div>
      )}
    </ChartCard>
  );
}
